/* ==========================================================================
   hsf-refined-motion-20260807.js
   Scroll-reveal driver for assets/hsf-refined-motion-20260807.css.

   Dependency-free. IntersectionObserver only - no animation-timeline path,
   because WebKit has not shipped scroll-driven animations on by default and
   iOS Safari is this store's primary surface.

   Contract with the inline gate in layout/theme.liquid:
     - the gate sets html[data-hsf-motion="on"] before first paint
     - the gate arms a 2200ms timer that REMOVES that attribute unless
       html[data-hsf-motion-ready] exists
     - this file's first act is to set data-hsf-motion-ready
   So if this file 404s, is CSP-blocked, or throws before start(), the gate
   withdraws the attribute and every hidden state in the CSS goes inert.

   The SELECTOR deliberately matches EVERY rise-grid child rather than
   mirroring the CSS's per-breakpoint :nth-child guards. CSS alone decides
   which children start hidden; the JS only stamps data-hsf-rise-in. Stamping
   a child that was never hidden is a no-op, and this makes it impossible for
   the two files to drift out of sync at a breakpoint.
   ========================================================================== */

(function () {
  'use strict';

  var root = document.documentElement;
  var SELECTOR = '[data-hsf-rise], [data-hsf-rise-grid] > *';

  function markReady() {
    root.setAttribute('data-hsf-motion-ready', '');
  }

  // The gate declined (no IntersectionObserver, or reduced motion). Nothing to
  // do, but still disarm the timer so it is not left spinning.
  if (root.getAttribute('data-hsf-motion') !== 'on') {
    markReady();
    return;
  }

  function reveal(el) {
    el.setAttribute('data-hsf-rise-in', '');
  }

  function revealAll(list) {
    for (var i = 0; i < list.length; i++) {
      if (!list[i].hasAttribute('data-hsf-rise-in')) reveal(list[i]);
    }
  }

  function collect(scope) {
    var found = (scope || document).querySelectorAll(SELECTOR);
    var out = [];
    for (var i = 0; i < found.length; i++) {
      if (!found[i].hasAttribute('data-hsf-rise-in')) out.push(found[i]);
    }
    return out;
  }

  function observeAll(list, observer) {
    var viewportHeight = window.innerHeight || root.clientHeight || 0;

    for (var i = 0; i < list.length; i++) {
      var el = list[i];

      // Already on screen when we boot: reveal with no transition at all, so a
      // tall viewport can never hold back paint.
      var rect = el.getBoundingClientRect();
      if (rect.top < viewportHeight && rect.bottom > 0) {
        el.setAttribute('data-hsf-rise-instant', '');
        reveal(el);
        continue;
      }

      observer.observe(el);
    }
  }

  function start() {
    markReady();

    var nodes = collect(document);
    if (!nodes.length) return;

    var observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          reveal(entries[i].target);
          observer.unobserve(entries[i].target);
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    observeAll(nodes, observer);

    // Failsafe. IntersectionObserver can legitimately never fire for a node
    // inside a display:none or content-visibility:auto ancestor - note
    // .footer-content in sections/footer.liquid uses content-visibility: auto.
    // Nothing stays hidden past three seconds.
    window.setTimeout(function () {
      revealAll(nodes);
    }, 3000);

    // Honour an OS reduced-motion flip, in BOTH directions. Flipping back off
    // must restore the hover scale, which is scoped under the same attribute.
    // Re-arming cannot re-hide anything: every node already carries
    // data-hsf-rise-in by the time we get here.
    var mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    var onPreferenceChange = function () {
      if (mq.matches) {
        observer.disconnect();
        revealAll(nodes);
        root.removeAttribute('data-hsf-motion');
      } else {
        root.setAttribute('data-hsf-motion', 'on');
      }
    };
    if (mq.addEventListener) mq.addEventListener('change', onPreferenceChange);
    else if (mq.addListener) mq.addListener(onPreferenceChange);

    // Theme editor: sections re-render without a page load. Each batch gets its
    // own failsafe, because the timer above closed over the boot-time array.
    document.addEventListener('shopify:section:load', function (event) {
      var fresh = collect(event.target);
      if (!fresh.length) return;
      for (var i = 0; i < fresh.length; i++) nodes.push(fresh[i]);
      observeAll(fresh, observer);
      window.setTimeout(function () {
        revealAll(fresh);
      }, 3000);
    });

    // Keep the array from growing without bound across editor add/remove.
    document.addEventListener('shopify:section:unload', function () {
      for (var i = nodes.length - 1; i >= 0; i--) {
        if (!nodes[i].isConnected) nodes.splice(i, 1);
      }
    });
  }

  try {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start, { once: true });
    } else {
      start();
    }
  } catch (error) {
    // Never leave the page hidden because of us.
    root.removeAttribute('data-hsf-motion');
    markReady();
  }
})();
